import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { formatCurrencyTotals } from "../../lib/money";
import CombinedEarnings from "../../components/finance/CombinedEarnings";

const PERIODS = [
  ["month_to_date", "This month"],
  ["last_30_days", "Last 30 days"],
  ["year_to_date", "Year to date"],
  ["all_time", "All time"],
];

const card = "rounded-xl border bg-black/60 p-5";
const cardStyle = { borderColor: "rgba(255, 16, 240, 0.25)", boxShadow: "0 0 24px -16px #FF10F0" };

function Totals({ value, testid }) {
  return <span data-testid={testid}>{formatCurrencyTotals(value, { decimals: 0, empty: "—", keepZero: true })}</span>;
}

// Money > Overview: GET /api/money/overview. Every figure is per currency;
// EUR and USD are never added together and nothing is converted or estimated.
export default function MoneyOverview() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const { data: body } = await api.get("/api/money/overview");
      setData(body);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load the money overview.");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (error) {
    return (
      <div className={card} style={cardStyle} data-testid="money-overview-error">
        <p className="text-sm text-red-300">{error}</p>
        <button type="button" onClick={load} className="mt-3 rounded border border-[#FF10F0]/40 px-3 py-1.5 text-sm text-white hover:bg-[#FF10F0]/10">Retry</button>
      </div>
    );
  }
  if (!data) return <p className="text-sm text-zinc-500" data-testid="money-overview-loading">Loading…</p>;

  const empty = data.empty || {};
  if (empty.all) {
    return (
      <div className={card} style={cardStyle} data-testid="money-overview-empty">
        <h2 className="text-lg font-semibold text-white">No money recorded yet</h2>
        <p className="mt-2 max-w-2xl text-sm text-zinc-400">
          There are no platform earnings, studio payouts or expenses in the database yet, so there is nothing to total.
          Nothing here is estimated or filled with sample data.
        </p>
        <div className="mt-4 flex flex-wrap gap-2 text-sm">
          <Link to="?tab=imports" className="rounded border border-[#FF10F0]/40 px-3 py-1.5 text-white hover:bg-[#FF10F0]/10">Import a statement</Link>
          <Link to="?tab=revenue" className="rounded border border-zinc-700 px-3 py-1.5 text-zinc-200 hover:border-[#FF10F0]/60">Log a studio payout</Link>
          <Link to="?tab=expenses" className="rounded border border-zinc-700 px-3 py-1.5 text-zinc-200 hover:border-[#FF10F0]/60">Log an expense</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="money-overview">
      <section className={card} style={cardStyle}>
        <div className="mb-2 text-xs font-bold uppercase tracking-wider text-[#FF10F0]">Earnings by platform</div>
        <CombinedEarnings combined={data.earnings} />
      </section>

      <section className={card} style={cardStyle}>
        <div className="mb-3 text-xs font-bold uppercase tracking-wider text-[#FF10F0]">Cash in and out</div>
        <div className="space-y-3" data-testid="money-periods">
          {PERIODS.map(([key, label]) => (
            <div key={key} className="grid gap-2 border-b border-zinc-900 pb-3 last:border-0 last:pb-0 sm:grid-cols-[minmax(7rem,0.7fr)_repeat(3,minmax(0,1fr))] sm:items-center" data-testid={`money-period-${key}`}>
              <div className="text-sm font-medium text-zinc-300">{label}</div>
              <div><div className="text-[10px] uppercase text-zinc-500">Studio payouts</div><div className="font-mono text-sm text-white"><Totals value={data.studio_payouts?.[key]} testid={`money-payouts-${key}`} /></div></div>
              <div><div className="text-[10px] uppercase text-zinc-500">Expenses</div><div className="font-mono text-sm text-white"><Totals value={data.expenses?.[key]} testid={`money-expenses-${key}`} /></div></div>
              <div><div className="text-[10px] uppercase text-zinc-500">Net (cash)</div><div className="font-mono text-sm text-white"><Totals value={data.net_cash?.[key]} testid={`money-net-${key}`} /></div></div>
            </div>
          ))}
        </div>
        <p className="mt-2 text-[11px] text-zinc-500">{data.notes?.studio_payouts} {data.notes?.net_cash} {data.notes?.currency}</p>
        {empty.studio_payouts && <p className="mt-1 text-[11px] text-zinc-500" data-testid="money-payouts-empty">No studio payouts recorded yet.</p>}
        {empty.expenses && <p className="mt-1 text-[11px] text-zinc-500" data-testid="money-expenses-empty">No expenses logged yet.</p>}
      </section>

      <section className={card} style={cardStyle} data-testid="money-sources">
        <div className="mb-2 text-xs font-bold uppercase tracking-wider text-[#FF10F0]">Where these numbers come from</div>
        <ul className="grid gap-1 text-sm text-zinc-300 sm:grid-cols-2">
          <li>Revenue ledger rows: {data.sources?.revenue_ledger_rows ?? 0}</li>
          <li>Extension earnings snapshots: {data.sources?.platform_metrics_earnings_docs ?? 0}</li>
          <li>Platform stats records: {data.sources?.platform_stats_docs ?? 0}</li>
          <li>Expense rows: {data.sources?.expense_rows ?? 0}</li>
          <li>Imported sales rows: {data.sources?.finance_sales ?? 0}</li>
          <li>Imported statements: {data.sources?.finance_statements ?? 0}</li>
        </ul>
        {empty.finance_imports && (
          <p className="mt-2 text-[11px] text-zinc-500" data-testid="money-imports-empty">No statements imported yet. Imports add per-sale detail; totals above do not depend on them.</p>
        )}
        <p className="mt-2 text-[11px] text-zinc-500">As of {data.generated_at ? new Date(data.generated_at).toLocaleString("en-GB", { timeZone: "Europe/Athens" }) : "—"} (Athens time).</p>
      </section>
    </div>
  );
}
